// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/components/RouteBoundary.tsx
// 🔧 PURPOSE: Le pagine si caricano a pezzi (lib/lazyPage). Qui si decide
//             cosa fare quando il pezzo di una pagina non arriva:
//             - con la rete, di solito è un rilascio nuovo: il file della
//               versione vecchia non c'è più. Si ricarica l'app UNA volta
//               (arriva la versione nuova), mai in giro tondo;
//             - offline, o se il tentativo non è bastato: un riquadro
//               "questa pagina non si è caricata" con "Ricarica", dentro
//               l'app (la barra in basso resta), non la schermata d'errore
//               di tutta l'app.
//             Gli altri errori salgono all'ErrorBoundary di sempre, come
//             prima. Cambiando pagina il riquadro sparisce.
//
//             Mentre il pezzo arriva (di rado: il percorso aperto si chiede
//             durante lo splash, le schede della barra a riposo) c'è una
//             rotellina al posto della pagina.
// ============================================

import React, { Suspense } from 'react';
import { useLocation } from 'react-router-dom';
import { Loader2, WifiOff } from 'lucide-react';
import { tr } from '../i18n';
import { retryFailedPages } from '../lib/lazyPage';

/** Il pezzo JS o CSS di una pagina non è arrivato (webpack / mini-css-extract). */
export function isChunkLoadError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const e = error as { name?: unknown; code?: unknown; message?: unknown };
  if (e.name === 'ChunkLoadError' || e.code === 'CSS_CHUNK_LOAD_FAILED') return true;
  return typeof e.message === 'string' && /Loading (CSS )?chunk [\w-]+ failed/i.test(e.message);
}

const RELOAD_KEY = 'nva:chunk-reload';
/** Un secondo tentativo entro questo tempo non ricarica più: si mostra il riquadro. */
const RELOAD_WINDOW_MS = 60_000;

/** Si può ricaricare da soli? Una volta sola per minuto (anche con sessionStorage bloccato). */
let reloadedInMemoryAt = 0;
export function claimChunkReload(now: number = Date.now()): boolean {
  let last = reloadedInMemoryAt;
  try {
    const saved = Number(window.sessionStorage.getItem(RELOAD_KEY) || 0);
    if (saved > last) last = saved;
  } catch {
    // sessionStorage negato: basta la memoria.
  }
  if (last && now - last < RELOAD_WINDOW_MS) return false;
  reloadedInMemoryAt = now;
  try {
    window.sessionStorage.setItem(RELOAD_KEY, String(now));
  } catch {
    // come sopra
  }
  return true;
}

export const PageLoading: React.FC = () => (
  <div role="status" aria-label={tr('common.loading')} className="flex min-h-[50vh] items-center justify-center">
    <Loader2 className="h-8 w-8 animate-spin text-gray-400 dark:text-gray-500" aria-hidden="true" />
  </div>
);

const PageUnavailable: React.FC = () => (
  <div role="alert" data-nva-page-unavailable="" className="flex min-h-[60vh] flex-col items-center justify-center gap-3 px-6 text-center">
    <WifiOff className="h-10 w-10 text-gray-400 dark:text-gray-500" aria-hidden="true" />
    <h2 className="m-0 text-lg font-bold text-gray-900 dark:text-white">{tr('app.pageUnavailable.title')}</h2>
    <p className="m-0 max-w-xs text-sm text-gray-600 dark:text-gray-400">{tr('app.pageUnavailable.desc')}</p>
    <button
      type="button"
      onClick={() => window.location.reload()}
      className="mt-2 min-h-[44px] rounded-xl bg-[#1e3a8a] px-6 font-semibold text-white"
    >
      {tr('common.reload')}
    </button>
  </div>
);

interface BoundaryProps {
  pathname: string;
  children: React.ReactNode;
  /** Solo per i test: al posto di window.location.reload. */
  onReload?: () => void;
}

interface BoundaryState {
  error: unknown;
}

class ChunkErrorBoundary extends React.Component<BoundaryProps, BoundaryState> {
  state: BoundaryState = { error: null };

  static getDerivedStateFromError(error: unknown): BoundaryState {
    return { error };
  }

  componentDidCatch(error: unknown) {
    if (!isChunkLoadError(error)) return;
    const online = typeof navigator === 'undefined' || navigator.onLine !== false;
    if (online && claimChunkReload()) {
      console.warn('[NVA] Pezzo della pagina non trovato: ricarico la versione nuova');
      (this.props.onReload || (() => window.location.reload()))();
    }
  }

  componentDidUpdate(prev: BoundaryProps) {
    if (prev.pathname !== this.props.pathname && this.state.error) {
      // Il pezzo fallito si riprova davvero alla prossima apertura della
      // sua pagina (React.lazy da solo rilancerebbe l'errore di prima).
      retryFailedPages();
      this.setState({ error: null });
    }
  }

  render() {
    const { error } = this.state;
    if (error) {
      // Non è un pezzo mancante: all'ErrorBoundary di tutta l'app, come prima.
      if (!isChunkLoadError(error)) throw error;
      return <PageUnavailable />;
    }
    return this.props.children;
  }
}

const RouteBoundary: React.FC<{ children: React.ReactNode; onReload?: () => void }> = ({ children, onReload }) => {
  const { pathname } = useLocation();
  return (
    <ChunkErrorBoundary pathname={pathname} onReload={onReload}>
      <Suspense fallback={<PageLoading />}>{children}</Suspense>
    </ChunkErrorBoundary>
  );
};

export default RouteBoundary;
